import {useEffect, useState} from 'react';

import type {FileBrowserFile, UseFileBrowserLoadFile} from './types';

type UseFileHook = {
  data: string;
  loading: boolean;
  error: string | undefined;
};

// This is a bit of a hack for better UX: Cache file results in a map so that
// they don't need to be refetched multiple times while moving between files in
// the file browser.
//
// Using a weakmap here so that if the FileBrowserFile _reference_ gets garbage
// collected, the cache entry will also be deleted. This allows us to keep file
// data around between components, without refetching the file, without needing
// to have manual set/free semantics.
//
// Note that this caching strategy will only be successful if FileBrowserFile
// is reference-stable across renders. In the case of the file browser, it is,
// but be cautious when making changes that might introduce instability here.
const USE_FILE_MEM_CACHE = new WeakMap<FileBrowserFile, string>();

type UseFileOptions = {
  skip?: boolean;
};

export function useFile(
  useLoadFile: UseFileBrowserLoadFile,
  file: FileBrowserFile,
  options: UseFileOptions,
  cache: WeakMap<FileBrowserFile, string> = USE_FILE_MEM_CACHE
): UseFileHook {
  const [data, setData] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const cached = cache.get(file);

  useEffect(() => {
    if (data !== '') {
      cache.set(file, data);
    }
  }, [data, file, cache]);

  const loading = useLoadFile(file, {
    ...options,
    skip: Boolean(cached || options.skip),
    onSuccess: setData,
    onFailure: (errorString?: string) =>
      setError(errorString ?? 'Error loading file'),
  });

  if (cached) {
    return {data: cached, loading: false, error: undefined};
  }

  return {data, loading, error};
}
